import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, PageError, PageLoading } from "../components/index.js";
import { useApi, useApiAction } from "../hooks/useApi.js";
import { NewProposalModal, ProposalStatusChip } from "./AgencyProposals.jsx";

/**
 * Prospect Pipeline (/agency/pipeline). Kanban + List toggle. Stage changes via
 * the detail-drawer dropdown (drag-and-drop deferred). Convert/Mark-Lost actions.
 */
const STAGES = [
  { key: "prospect", label: "Prospect" },
  { key: "proposal_sent", label: "Proposal Sent" },
  { key: "negotiating", label: "Negotiating" },
  { key: "contract_sent", label: "Contract Sent" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
];
const fmtMoney = (v) => (v == null ? "—" : `$${Number(v).toLocaleString()}`);

export function ProspectPipeline() {
  const api = useApiAction();
  const { data, loading, error, reload } = useApi("/api/prospects");
  const prospects = useMemo(() => data?.prospects || [], [data]);
  const [view, setView] = useState("kanban");
  const [selected, setSelected] = useState(null);
  const [adding, setAdding] = useState(false);
  const [actionError, setActionError] = useState(null); // surfaced inside the drawer

  const byStage = useMemo(() => {
    const m = Object.fromEntries(STAGES.map((s) => [s.key, []]));
    for (const p of prospects) (m[p.stage] ||= []).push(p);
    return m;
  }, [prospects]);

  function open(p) {
    setActionError(null);
    setSelected(p);
  }

  async function patch(id, body) {
    setActionError(null);
    try {
      const r = await api(`/api/prospects/${id}`, { method: "PUT", body: JSON.stringify(body) });
      // Silent: keep the board on screen while the fresh list swaps in.
      const fresh = await reload({ silent: true });
      // Refresh the drawer's prospect so the stage dropdown holds and the
      // Won-only actions (Convert to Client) appear without reopening.
      // Prefer the PUT response body; fall back to the reloaded list.
      const updated = r?.prospect || fresh?.prospects?.find((p) => p.id === id) || null;
      setSelected((sel) => (sel && sel.id === id && updated ? updated : sel));
    } catch (e) {
      setActionError(e);
    }
  }
  async function convert(id) {
    setActionError(null);
    try {
      await api(`/api/prospects/${id}/convert`, { method: "POST" });
      setSelected(null);
      await reload({ silent: true });
    } catch (e) {
      setActionError(e);
    }
  }

  if (error) return <PageError error={error} onRetry={() => reload()} />;
  if (loading) return <PageLoading />;

  return (
    <div className="max-w-[1300px]">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-[12px] text-text-muted">{`${prospects.length} prospects`}</p>
        <div className="flex items-center gap-2">
          <div className="flex rounded-card border border-border-default overflow-hidden text-[12px]">
            <button type="button" onClick={() => setView("kanban")} className={`px-3 py-1.5 ${view === "kanban" ? "bg-accent text-white" : "text-text-secondary"}`}>Kanban</button>
            <button type="button" onClick={() => setView("list")} className={`px-3 py-1.5 ${view === "list" ? "bg-accent text-white" : "text-text-secondary"}`}>List</button>
          </div>
          <button type="button" onClick={() => setAdding(true)} className="px-3 py-1.5 rounded-card bg-accent text-white text-[12px] font-medium">+ Add Prospect</button>
        </div>
      </div>

      {view === "kanban" ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {STAGES.map((s) => (
            <div key={s.key} className="bg-app-bg rounded-card p-2 min-h-[120px]">
              <div className="text-[11px] uppercase tracking-wide text-text-muted px-1 pb-2">{s.label} · {byStage[s.key].length}</div>
              <div className="space-y-2">
                {byStage[s.key].map((p) => (
                  <button key={p.id} type="button" onClick={() => open(p)} className="w-full text-left">
                    <Card className="px-3 py-2.5 hover:border-accent transition-colors">
                      <div className="text-[13px] font-medium text-text-primary truncate">{p.name}</div>
                      <div className="text-[11px] text-text-muted">{p.contactName || "—"}</div>
                      <div className="text-[11px] text-text-secondary mt-1">{fmtMoney(p.estimatedValue)}/mo</div>
                    </Card>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Card className="p-0">
          <table className="w-full text-[13px]">
            <thead><tr className="text-left text-text-muted text-[11px] uppercase tracking-wide border-b border-border-light">
              <th className="px-4 py-2">Name</th><th className="px-4 py-2">Contact</th><th className="px-4 py-2">Stage</th><th className="px-4 py-2">Est. Value</th>
            </tr></thead>
            <tbody>
              {prospects.map((p) => (
                <tr key={p.id} onClick={() => open(p)} className="border-b border-border-light hover:bg-app-bg cursor-pointer">
                  <td className="px-4 py-2 font-medium text-text-primary">{p.name}</td>
                  <td className="px-4 py-2 text-text-secondary">{p.contactName || "—"}</td>
                  <td className="px-4 py-2 text-text-secondary">{STAGES.find((s) => s.key === p.stage)?.label}</td>
                  <td className="px-4 py-2 text-text-secondary">{fmtMoney(p.estimatedValue)}/mo</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {selected && (
        <ProspectDrawer
          prospect={selected}
          error={actionError}
          onClose={() => { setActionError(null); setSelected(null); }}
          onStage={(stage) => patch(selected.id, { stage })}
          onLost={(lostReason) => patch(selected.id, { stage: "lost", lostReason })}
          onConvert={() => convert(selected.id)}
        />
      )}
      {adding && <AddProspectModal onClose={() => setAdding(false)} onCreated={async () => { setAdding(false); await reload({ silent: true }); }} />}
    </div>
  );
}

function ProspectDrawer({ prospect, error, onClose, onStage, onLost, onConvert }) {
  const [lostReason, setLostReason] = useState("");
  const canMarkLost = lostReason.trim().length > 0;
  const [proposing, setProposing] = useState(false);
  const navigate = useNavigate();
  const { data: detail } = useApi(`/api/prospects/${prospect.id}`);
  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/20" />
      <div className="relative w-[420px] max-w-full h-full bg-surface shadow-xl p-5 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="text-[12px] text-text-muted mb-3">✕ Close</button>
        <h2 className="text-[16px] font-semibold text-text-primary">{prospect.name}</h2>
        <p className="text-[12px] text-text-muted">{prospect.contactName || "—"} · {prospect.contactEmail || "no email"}</p>

        <label className="block mt-4 text-[11px] uppercase tracking-wide text-text-muted">Stage</label>
        <select value={prospect.stage} onChange={(e) => onStage(e.target.value)} className="mt-1 w-full border border-border-default rounded-card px-2 py-1.5 text-[13px]">
          {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>

        <div className="mt-3 text-[13px] text-text-secondary">Est. value: {fmtMoney(prospect.estimatedValue)}/mo</div>

        <div className="mt-4">
          <div className="flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wide text-text-muted">Proposals</span>
            {prospect.stage !== "lost" && (
              <button type="button" onClick={() => setProposing(true)} className="text-[12px] text-accent hover:underline">+ Create proposal</button>
            )}
          </div>
          {(detail?.proposals || []).length === 0 ? (
            <p className="mt-1 text-[12px] text-text-muted">None yet.</p>
          ) : (
            <ul className="mt-1 space-y-1">
              {detail.proposals.map((p) => (
                <li key={p.id}>
                  <button type="button" onClick={() => navigate(`/agency/proposals/${p.id}`)} className="w-full flex items-center justify-between text-[12px] hover:underline">
                    <span className="truncate">{p.title}</span>
                    <span className="inline-flex items-center gap-2 text-text-muted"><ProposalStatusChip status={p.status} />{fmtMoney(p.totalMonthlyRecurring)}/mo</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {proposing && (
          <NewProposalModal initial={{ prospectId: prospect.id, title: `${prospect.name} — proposal` }} onClose={() => setProposing(false)} onCreated={(id) => navigate(`/agency/proposals/${id}`)} />
        )}

        {prospect.notes && <p className="mt-3 text-[13px] text-text-secondary whitespace-pre-wrap">{prospect.notes}</p>}

        {error && (
          <p className="mt-3 text-[12px] text-negative">{error.message || "Something went wrong — try again."}</p>
        )}

        <div className="mt-5 space-y-2">
          {prospect.stage === "won" && (
            <button type="button" onClick={onConvert} className="w-full px-3 py-2 rounded-card bg-accent text-white text-[13px] font-medium">Convert to Client</button>
          )}
          {prospect.stage !== "lost" && (
            <div className="flex gap-2">
              <input value={lostReason} onChange={(e) => setLostReason(e.target.value)} placeholder="Lost reason (required)" className="flex-1 border border-border-default rounded-card px-2 py-1.5 text-[12px]" />
              <button
                type="button"
                disabled={!canMarkLost}
                onClick={() => canMarkLost && onLost(lostReason.trim())}
                className="px-3 py-1.5 rounded-card border border-border-default text-[12px] disabled:opacity-50"
              >
                Mark Lost
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AddProspectModal({ onClose, onCreated }) {
  const api = useApiAction();
  const [form, setForm] = useState({ name: "", contactName: "", contactEmail: "", website: "", industry: "", estimatedValue: "", source: "inbound" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit() {
    setBusy(true); setErr(null);
    try {
      const body = { ...form };
      if (body.estimatedValue) body.estimatedValue = Number(body.estimatedValue); else delete body.estimatedValue;
      for (const k of Object.keys(body)) if (body[k] === "") delete body[k];
      await api("/api/prospects", { method: "POST", body: JSON.stringify(body) });
      await onCreated();
    } catch (e) { setErr(e); setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative w-[440px] max-w-full bg-surface rounded-card shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-[15px] font-semibold text-text-primary mb-3">Add Prospect</h2>
        {err && <p className="text-[12px] text-red-600 mb-2">{err.message}</p>}
        <div className="space-y-2">
          {[["name","Business name *"],["contactName","Contact name"],["contactEmail","Contact email"],["website","Website"],["industry","Industry"],["estimatedValue","Est. monthly value ($)"]].map(([k, label]) => (
            <input key={k} value={form[k]} onChange={set(k)} placeholder={label} className="w-full border border-border-default rounded-card px-2 py-1.5 text-[13px]" />
          ))}
          <select value={form.source} onChange={set("source")} className="w-full border border-border-default rounded-card px-2 py-1.5 text-[13px]">
            {["inbound","outbound","referral","existing_client"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-[13px] text-text-secondary">Cancel</button>
          <button type="button" disabled={busy || !form.name} onClick={submit} className="px-3 py-1.5 rounded-card bg-accent text-white text-[13px] font-medium disabled:opacity-50">{busy ? "Saving…" : "Create"}</button>
        </div>
      </div>
    </div>
  );
}
